import { DropDown, selectOptionsToDropDown, useErrorState } from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/dictionaryApi';
import { getOrderStatusesSelectList } from 'axios/orderStatusApi';
import { getTradeOrgsList } from 'axios/tradeOrgsApi';
import { getOperatorsUserList } from 'axios/usersApi';
import useApi from 'hooks/useApi';
import { Dictionary } from 'lodash';
import { SelectOption } from 'models/api/prop';
import { UserShort } from 'models/api/users';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, UseQueryResult } from 'react-query';
import useAuth from 'hooks/useAuth';

type OrderListsProviderProps = {
    orderStatuses: DropDown[];
    loanStatuses: DropDown[];
    operators: UserShort[];
    tradeOrgs: DropDown[];
    brandCodes: DropDown[];
    productTypes: DropDown[];
    sources: DropDown[];
    queryDicts: UseQueryResult<Dictionary<DropDown[]>, unknown>;
    queryOrderStatuses: UseQueryResult<DropDown[], unknown>;
    queryOperators: UseQueryResult<UserShort[], unknown>;
    queryTradeOrgs: UseQueryResult<SelectOption[], unknown>;
    queryBrandCodes: UseQueryResult<Dictionary<DropDown[]>, unknown>;
    queryProductTypes: UseQueryResult<Dictionary<DropDown[]>, unknown>;
};

const OrderListsContext = createContext<OrderListsProviderProps>({} as OrderListsProviderProps);

export type OrderStatusesProviderProps = {
    children: React.ReactNode;
};

const OrderListsProvider = ({ children }: OrderStatusesProviderProps) => {
    const [orderStatuses, setOrderStatuses] = useState<DropDown[]>([]);
    const [loanStatuses, setLoanStatuses] = useState<DropDown[]>([]);
    const [operators, setOperators] = useState<UserShort[]>([]);
    const [tradeOrgs, setTradeOrgs] = useState<DropDown[]>([]);
    const [sources, setSources] = useState<DropDown[]>([]);

    const { i18n } = useTranslation();
    const api = useApi();
    const { hasRole } = useAuth();
    const { handleRequestError } = useErrorState();

    const queryOrderStatuses = useQuery(
        ['orderStatusesSelectList', i18n.language],
        () => getOrderStatusesSelectList(api, false),
        {
            onSuccess: (data) => setOrderStatuses(data),
            onError: (e) => handleRequestError(e),
        }
    );

    const queryDicts = useQuery(
        ['loanStatusesSelectList', i18n.language],
        () => getSelectList(api, ['LoanStatus', 'TOIntegrationType']),
        {
            onSuccess: (data) => {
                setLoanStatuses(data['LoanStatus'] ?? []);
                setSources(data['TOIntegrationType'] ?? []);
            },
            onError: (e) => handleRequestError(e),
        }
    );

    const queryOperators = useQuery('usersList', () => getOperatorsUserList(api), {
        onSuccess: (data) => setOperators(data),
        enabled: !operators.length,
    });

    const queryTradeOrgs = useQuery('tradeOrgs', () => getTradeOrgsList(api), {
        onSuccess: (data) => setTradeOrgs(selectOptionsToDropDown(data)),
        onError: (e) => handleRequestError(e),
        enabled: !tradeOrgs.length,
    });

    const queryBrandCodes = useQuery(
        ['brandCodes', i18n.language],
        () => getSelectList(api, ['BrandCode']),
        {
            onError: (e) => handleRequestError(e),
            enabled: hasRole('visibleBrandCodesFilter'),
        }
    );
    const brandCodes = useMemo<DropDown[]>(() => {
        return queryBrandCodes.data?.['BrandCode'] ?? [];
    }, [queryBrandCodes.data]);

    const queryProductTypes = useQuery(
        ['productTypes', i18n.language],
        () => getSelectList(api, ['ProductTypes']),
        {
            onError: (e) => handleRequestError(e),
        }
    );
    const productTypes = useMemo<DropDown[]>(() => {
        return queryProductTypes.data?.['ProductTypes'] ?? [];
    }, [queryProductTypes.data]);

    const providerProps = useMemo(
        () => ({
            orderStatuses,
            loanStatuses,
            operators,
            tradeOrgs,
            brandCodes,
            productTypes,
            sources,
            queryOperators,
            queryDicts,
            queryOrderStatuses,
            queryTradeOrgs,
            queryBrandCodes,
            queryProductTypes,
        }),
        [
            orderStatuses,
            loanStatuses,
            operators,
            tradeOrgs,
            brandCodes,
            productTypes,
            sources,
            queryOperators,
            queryDicts,
            queryOrderStatuses,
            queryTradeOrgs,
            queryBrandCodes,
            queryProductTypes,
        ]
    );

    return (
        <OrderListsContext.Provider value={providerProps}>{children}</OrderListsContext.Provider>
    );
};

const useOrderLists = () => {
    const context = useContext(OrderListsContext);

    return context;
};

export { OrderListsProvider, useOrderLists };
